import React, { useState } from 'react';

const BulkQuestionUpload = ({ onQuestionsAdded, onClose }) => {
  const [uploadMethod, setUploadMethod] = useState('form'); // 'form' or 'file'
  const [bulkText, setBulkText] = useState('');
  const [parsedQuestions, setParsedQuestions] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');

  // Sample format for bulk questions
  const sampleFormat = `Question 1: What is the capital of Zambia?
A. Lusaka
B. Harare
C. Johannesburg
D. Nairobi
Correct: A
Marks: 1
Type: multiple_choice

Question 2: True or False: The sun rises in the east.
Correct: True
Marks: 1
Type: true_false

Question 3: Solve: 2 + 2 = ?
Correct: 4
Marks: 2
Type: short_answer`;

  const parseBulkText = (text) => {
    const questions = [];
    const lines = text.split('\n').filter(line => line.trim());
    
    let currentQuestion = null;
    let questionText = '';
    let options = [];
    let correctAnswer = null;
    let marks = 1;
    let type = 'multiple_choice';
    let questionNumber = 0;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Start of new question
      if (line.match(/^Question\s*\d+:/i)) {
        // Save previous question if exists
        if (currentQuestion) {
          // Validate and fix the question before adding
          const validatedQuestion = validateAndFixQuestion({
            id: `bulk_${Date.now()}_${questionNumber}`,
            question: questionText.trim(),
            options: options,
            correctAnswer: correctAnswer,
            marks: marks,
            type: type
          });
          
          if (validatedQuestion) {
            questions.push(validatedQuestion);
          }
        }
        
        // Start new question
        questionText = line.replace(/^Question\s*\d+:\s*/i, '');
        options = [];
        correctAnswer = null;
        marks = 1;
        type = 'multiple_choice';
        currentQuestion = true;
        questionNumber++;
      }
      // Multiple choice options (support any letter option)
      else if (line.match(/^[A-Z]\.\s*/)) {
        const option = line.replace(/^[A-Z]\.\s*/, '');
        options.push(option);
      }
      // Correct answer
      else if (line.match(/^Correct:\s*/i)) {
        const answer = line.replace(/^Correct:\s*/i, '').trim();
        if (type === 'multiple_choice') {
          // Convert letter to index, but we'll validate later
          correctAnswer = answer.toUpperCase();
        } else {
          correctAnswer = answer.toLowerCase() === 'true' ? true : 
                         answer.toLowerCase() === 'false' ? false : answer;
        }
      }
      // Marks
      else if (line.match(/^Marks:\s*/i)) {
        marks = parseInt(line.replace(/^Marks:\s*/i, '').trim()) || 1;
      }
      // Question type
      else if (line.match(/^Type:\s*/i)) {
        type = line.replace(/^Type:\s*/i, '').trim().toLowerCase();
      }
      // Continuation of question text (for multi-line questions)
      else if (currentQuestion && !line.match(/^(Correct|Marks|Type):/i) && !line.match(/^[A-Z]\./)) {
        questionText += ' ' + line;
      }
    }
    
    // Add the last question
    if (currentQuestion) {
      const validatedQuestion = validateAndFixQuestion({
        id: `bulk_${Date.now()}_${questionNumber}`,
        question: questionText.trim(),
        options: options,
        correctAnswer: correctAnswer,
        marks: marks,
        type: type
      });
      
      if (validatedQuestion) {
        questions.push(validatedQuestion);
      }
    }
    
    return questions;
  };

  const validateAndFixQuestion = (question) => {
    // Basic validation
    if (!question.question || question.question.trim().length < 5) {
      console.warn('Invalid question text:', question);
      return null;
    }

    // Handle different question types
    if (question.type === 'multiple_choice') {
      // Ensure we have options
      if (!question.options || question.options.length < 2) {
        console.warn('Multiple choice question needs at least 2 options:', question);
        return null;
      }

      // Convert letter answer to index
      if (typeof question.correctAnswer === 'string') {
        const letterIndex = question.correctAnswer.charCodeAt(0) - 65; // A=0, B=1, etc.
        if (letterIndex >= 0 && letterIndex < question.options.length) {
          question.correctAnswer = letterIndex;
        } else {
          console.warn('Invalid correct answer for multiple choice:', question);
          return null;
        }
      }
    } else if (question.type === 'true_false') {
      // Ensure correctAnswer is boolean
      if (typeof question.correctAnswer !== 'boolean') {
        question.correctAnswer = question.correctAnswer === 'true';
      }
      // True/false questions don't need options
      question.options = [];
    } else if (question.type === 'short_answer') {
      // Short answer questions don't need options
      question.options = [];
      // Ensure correctAnswer is a string
      if (typeof question.correctAnswer !== 'string') {
        question.correctAnswer = String(question.correctAnswer);
      }
    }

    // Validate marks
    if (!question.marks || question.marks < 1) {
      question.marks = 1;
    }

    return question;
  };

  const handleParseQuestions = () => {
    setIsProcessing(true);
    setError('');
    
    try {
      console.log('🔍 Parsing bulk questions from text:', bulkText.substring(0, 200) + '...');
      const questions = parseBulkText(bulkText);
      
      console.log('📊 Parsed questions:', questions);
      
      if (questions.length === 0) {
        setError('No valid questions found. Please check your format.');
        setParsedQuestions([]);
      } else {
        setParsedQuestions(questions);
        setError('');
        console.log(`✅ Successfully parsed ${questions.length} questions`);
      }
    } catch (err) {
      console.error('❌ Error parsing questions:', err);
      setError('Error parsing questions: ' + err.message);
      setParsedQuestions([]);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      setBulkText(event.target.result);
      setUploadMethod('form');
    };
    reader.readAsText(file);
  };

  const handleAddQuestions = () => {
    if (parsedQuestions.length === 0) {
      setError('Please parse questions first');
      return;
    }
    
    console.log('🚀 Adding questions to exam:', parsedQuestions);
    onQuestionsAdded(parsedQuestions);
    onClose();
  };

  const downloadTemplate = () => {
    const blob = new Blob([sampleFormat], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bulk_questions_template.txt';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="bulk-question-upload">
      <div className="bulk-upload-header">
        <h3>Bulk Question Upload</h3>
        <div className="upload-method-tabs">
          <button
            className={`tab-btn ${uploadMethod === 'form' ? 'active' : ''}`}
            onClick={() => setUploadMethod('form')}
          >
            Text Entry
          </button>
          <button
            className={`tab-btn ${uploadMethod === 'file' ? 'active' : ''}`}
            onClick={() => setUploadMethod('file')}
          >
            File Upload
          </button>
        </div>
      </div>

      <div className="bulk-upload-content">
        {uploadMethod === 'form' && (
          <div className="text-entry-section">
            <div className="format-help">
              <div className="help-header">
                <h4>Format Instructions:</h4>
                <button onClick={downloadTemplate} className="btn btn-sm btn-secondary">
                  Download Template
                </button>
              </div>
              <pre className="format-sample">{sampleFormat}</pre>
            </div>

            <div className="bulk-text-area">
              <label>Paste your questions below:</label>
              <textarea
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                placeholder="Paste your questions in the format shown above..."
                rows={15}
                className="bulk-textarea"
              />
            </div>

            <div className="parse-actions">
              <button
                onClick={handleParseQuestions}
                disabled={!bulkText.trim() || isProcessing}
                className="btn btn-primary"
              >
                {isProcessing ? 'Parsing...' : 'Parse Questions'}
              </button>
            </div>
          </div>
        )}

        {uploadMethod === 'file' && (
          <div className="file-upload-section">
            <div className="format-help">
              <h4>Upload a text file with questions in the correct format</h4>
              <button onClick={downloadTemplate} className="btn btn-sm btn-secondary">
                Download Template
              </button>
            </div>

            <div className="file-input-area">
              <input
                type="file"
                accept=".txt,.csv"
                onChange={handleFileUpload}
                className="file-input"
              />
              <p>Upload a .txt or .csv file with questions in the correct format</p>
            </div>
          </div>
        )}

        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            {error}
          </div>
        )}

        {parsedQuestions.length > 0 && (
          <div className="parsed-questions">
            <h4>Parsed Questions ({parsedQuestions.length})</h4>
            <div className="questions-preview">
              {parsedQuestions.map((question, index) => (
                <div key={question.id} className="question-preview-item">
                  <div className="question-header">
                    <span className="question-number">Q{index + 1}</span>
                    <span className="question-meta">
                      {question.type} • {question.marks} marks
                    </span>
                  </div>
                  <p className="question-text">{question.question}</p>
                  {question.type === 'multiple_choice' && (
                    <div className="options-preview">
                      {question.options.map((option, i) => (
                        <div key={i} className={`option ${i === question.correctAnswer ? 'correct' : ''}`}>
                          {String.fromCharCode(65 + i)}. {option}
                          {i === question.correctAnswer && ' ✓'}
                        </div>
                      ))}
                    </div>
                  )}
                  {question.type !== 'multiple_choice' && (
                    <div className="answer-preview">
                      <strong>Answer:</strong> {question.correctAnswer}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="add-questions-action">
              <button
                onClick={handleAddQuestions}
                className="btn btn-primary btn-large"
              >
                Add {parsedQuestions.length} Questions to Exam
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="bulk-upload-footer">
        <button onClick={onClose} className="btn btn-secondary">
          Cancel
        </button>
      </div>
    </div>
  );
};

export default BulkQuestionUpload;
